/**
 * Works out the real file type (mime + extension) of a base64-encoded
 * document coming back from the API, so downloads always land with the
 * same extension the file was uploaded with — never a generic `.zip`.
 *
 * Shared by every screen that downloads a stored document (Document,
 * Document History — used by both Onboarding and Leads) so the detection
 * logic lives in exactly one place.
 */

/** Extension -> MIME, for names that already say what the file is. */
const EXT_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  csv: 'text/csv',
  txt: 'text/plain',
  zip: 'application/zip',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
};

export interface DocumentFileInfo {
  mimeType: string;
  extension: string;
}

/** Strips a `data:...;base64,` prefix, surrounding quotes and whitespace. */
export function cleanBase64(value: string): string {
  if (!value) {
    return '';
  }

  let base64 = String(value).trim();

  const commaIndex = base64.indexOf(',');

  if (base64.startsWith('data:') && commaIndex !== -1) {
    base64 = base64.substring(commaIndex + 1);
  }

  return base64.replace(/^["']|["']$/g, '').replace(/\s/g, '');
}

export function getFileNameWithoutExtension(fileName: string): string {
  return (fileName || 'document').replace(/\.[^/.]+$/, '');
}

/**
 * Which OOXML format a ZIP holds, from the part names in its entry headers.
 * Those names are stored uncompressed, so they read straight out of the raw
 * bytes without unzipping anything.
 */
function detectOoxml(binary: string): DocumentFileInfo {
  if (binary.includes('word/')) {
    return { mimeType: EXT_MIME['docx'], extension: 'docx' };
  }

  if (binary.includes('xl/')) {
    return { mimeType: EXT_MIME['xlsx'], extension: 'xlsx' };
  }

  if (binary.includes('ppt/')) {
    return { mimeType: EXT_MIME['pptx'], extension: 'pptx' };
  }

  return { mimeType: EXT_MIME['zip'], extension: 'zip' };
}

/**
 * Which legacy Office format an OLE2 file holds, from its stream names.
 * Those are stored as UTF-16, so the ASCII letters sit between NUL bytes —
 * stripping them is enough to match on.
 */
function detectOle2(binary: string): DocumentFileInfo {
  const names = binary.split(String.fromCharCode(0)).join('');

  if (names.includes('WordDocument')) {
    return { mimeType: EXT_MIME['doc'], extension: 'doc' };
  }

  if (names.includes('Workbook') || names.includes('Book')) {
    return { mimeType: EXT_MIME['xls'], extension: 'xls' };
  }

  if (names.includes('PowerPoint')) {
    return { mimeType: EXT_MIME['ppt'], extension: 'ppt' };
  }

  // Unknown OLE2 payload — Word is the commonest, and was the old default.
  return { mimeType: EXT_MIME['doc'], extension: 'doc' };
}

/**
 * Works out what a stored document actually is.
 *
 * `fileName` wins when it carries a known extension — the file said what it
 * was on the way in, and no amount of sniffing beats that. Otherwise the
 * bytes are inspected.
 *
 * Office formats need more than a magic number: .docx, .xlsx and .pptx are
 * all ZIP containers starting "PK", and .doc, .xls and .ppt are all OLE2
 * compound files starting D0 CF 11 E0. Guessing from the first four bytes
 * hands Excel a .docx and Word an .xls, so the container is looked into —
 * ZIP entry names ("xl/", "word/", "ppt/") and OLE2 stream names, both of
 * which sit in plain sight near the start of the file (well past the tiny
 * 150-byte sniff a naive check would take, which is why that used to
 * misidentify every Office document as a plain `.zip`).
 */
export function getFileInfo(base64Value: string, fileName = ''): DocumentFileInfo {
  const named = (fileName.match(/\.([A-Za-z0-9]+)$/)?.[1] ?? '').toLowerCase();

  if (named && EXT_MIME[named]) {
    return { mimeType: EXT_MIME[named], extension: named };
  }

  const base64 = cleanBase64(base64Value);

  if (!base64) {
    return { mimeType: 'application/octet-stream', extension: 'bin' };
  }

  try {
    // Enough to reach the container's entry/stream names, not just its magic
    // number. Trimmed to a multiple of 4 so atob gets whole base64 groups.
    const sampleLength = Math.min(base64.length, 8192) & ~3;
    const binary = atob(base64.substring(0, sampleLength));
    const bytes = Array.from(binary).map((char) => char.charCodeAt(0));

    // JPEG: FF D8 FF
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return { mimeType: 'image/jpeg', extension: 'jpg' };
    }

    // PNG: 89 50 4E 47 0D 0A 1A 0A
    if (
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    ) {
      return { mimeType: 'image/png', extension: 'png' };
    }

    // PDF: %PDF
    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      return { mimeType: 'application/pdf', extension: 'pdf' };
    }

    // GIF: GIF87a / GIF89a
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      return { mimeType: 'image/gif', extension: 'gif' };
    }

    // WEBP: RIFF....WEBP
    if (
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return { mimeType: 'image/webp', extension: 'webp' };
    }

    // OLE2 compound file: D0 CF 11 E0 — .doc, .xls or .ppt.
    if (bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) {
      return detectOle2(binary);
    }

    // ZIP container: PK — .docx, .xlsx, .pptx or a plain archive.
    if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
      return detectOoxml(binary);
    }
  } catch (error) {
    console.error('File type detection failed:', error);
  }

  return { mimeType: 'application/octet-stream', extension: 'bin' };
}
